// Percorso: src/pages/EnrolFamily.jsx
//
// PERCORSO B — IL GENITORE CHE ISCRIVE I MINORI DELLA FAMIGLIA (RL-5a).
//
// È la controparte di frontend del `POST /enrol-family` nato in RL-3b/RL-3c, e
// il gemello di `Enrol.jsx` (percorso A, RL-4): stessa pagina, stesso stile,
// stessi componenti, stesse regole sul modo di dire com'è andata. Non è un
// restyling di `RegisterMulti.jsx`, poi rimosso da RL-5b: è la stessa
// FUNZIONE rifatta sul modello nuovo.
//
// CHE COSA CAMBIA RISPETTO A RegisterMulti.jsx
//
//   • UNA SOLA CHIAMATA, ATOMICA. Il modulo vecchio faceva due registrazioni
//     in sequenza (`/register-parent`, poi `/register-child` una per figlio) e
//     lasciava famiglie a metà quando la seconda falliva. Qui parte una sola
//     `POST /enrol-family` con `{ parent, children }`: il server scrive tutto
//     in un batch, o nascono tutti o non nasce nessuno.
//
//   • NIENTE PASSWORD e nessuna `createUserWithEmailAndPassword` (strada (b),
//     D9): non si crea nessuna utenza Firebase, quindi non c'è nemmeno il
//     rollback cieco del modulo vecchio. L'ingresso arriverà col link di RL-8.
//
//   • NIENTE DATA DI NASCITA DEL GENITORE (D6): al club non serve, e senza di
//     essa il costruttore lo considera adulto. Il modulo non la chiede: un
//     campo che non si usa è un dato personale raccolto per niente.
//
//   • NIENTE EMAIL DEL MINORE, NIENTE UPLOAD, NIENTE ffaNumber, niente
//     indirizzo, niente contatti di emergenza (D11, D12): i minori non hanno
//     casella propria (si scrive al genitore), e i documenti sono materia
//     della finestra di completamento (RL-7).
//
//   • I TERMINI DEL MINORE LI ACCETTA IL GENITORE, una spunta per ciascun
//     minore. Il server registra sul documento del bambino CHI ha accettato
//     per lui (`acceptedBy: guardian:<id>`), e nell'email di conferma finisce
//     l'elenco dei nomi per conto dei quali si è firmato.
//
// LE VALIDAZIONI DI QUESTA PAGINA SONO CORTESIA, NON LEGGE. L'autorità è il
// server: il tetto dei dieci minori, lo sbarramento del "figlio" maggiorenne,
// il vocabolario di `requestedContext` e il duplicato di email vivono in
// `POST /enrol-family`, che è pubblica e risponde allo stesso modo anche a chi
// la chiama a mano. Qui si evita solo alla famiglia il giro inutile.

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/api";
// ⚠️ RL-8c: `PUBLIC_APP_URL` serviva solo alla frase sull'email di istruzioni,
// tolta perché quell'email non parte. Stessa correzione del ramo adulto
// (Enrol.jsx), e per la stessa ragione.
import {
  GENERI,
  emailBenFormata,
  etaCompiuta,
  messaggioDiErrore,
  oggiAPerth,
} from "../utils/enrolDati";
import { LinkTermini, TornaAllaScelta } from "./enrolShared";
// LA VESTE (cantiere «grafica enrollment», passo 1): gli stessi tre fogli del
// ramo adulto, e per le stesse ragioni — vedi il commento d'importazione in
// `Enrol.jsx`. `Register.css` è stato ritirato.
import "../components/HomeShell.css";
import "../components/AdminTools.css";
import "./Enrolment.css";
// LE LINGUE D'AIUTO (cantiere «enrollment» 2). Come nel ramo adulto: le parole
// vengono dal dizionario unico — le stesse, alla lettera — e sotto ciascuna può
// comparire la riga d'aiuto. Il contenitore di stato NON si ripete qui: questo
// ramo vive dentro la pagina `Enrol`, che lo porta già.
import { testo as t } from "./enrolLingue";
import { Aiuto, BarraLingue, RigaAvviso } from "./enrolAiuto";

// Lo stesso tetto del server (MAX_FIGLI_PER_ISCRIZIONE in backend/server.js).
// Qui serve solo a non far compilare a mano un undicesimo riquadro destinato
// a essere respinto: l'autorità resta la rotta, che risponde 400.
export const MAX_FIGLI = 10;

// I SOLI campi di un minore. Questo oggetto è anche l'elenco di ciò che parte
// verso il server: quello che non c'è qui non si raccoglie e non si manda.
// NIENTE email, NIENTE upload, NIENTE ruolo (i minori nascono Player).
function figlioVuoto() {
  return {
    firstName: "",
    lastName: "",
    dob: "",
    gender: "",
    acceptTerms: false,
  };
}

// Lo stato dell'intero modulo famiglia. È una FUNZIONE e non una costante
// perché contiene un array: una costante di modulo verrebbe condivisa fra un
// modulo e il successivo.
export function famigliaVuota() {
  return {
    // I 5 campi di D5 per l'adulto, meno la data di nascita (D6) e meno il
    // ruolo (qui il ruolo è "Parent", lo scrive il server).
    parent: {
      firstName: "",
      lastName: "",
      email: "",
      gender: "",
      acceptTerms: false,
    },
    // La spunta «gioco/alleno anch'io» (D7) e la scelta che apre.
    ancheIo: false,
    requestedContext: "",
    // Variante «i minori sono già stati iscritti da un altro genitore»
    // (D8, fase 1): se vera il modulo manda `children: []`. Nel modulo non
    // c'è una spunta che la accenda: resta `false`, e i rami che la leggono
    // più sotto sono dormienti.
    giaIscritti: false,
    children: [figlioVuoto()],
  };
}

// Le due sole parole che `POST /enrol-family` accetta in `requestedContext`.
// Si mandano già maiuscole, che è la grafia riconosciuta dal costruttore: la
// rotta le maiuscolizza comunque, ma un modulo che manda la forma giusta non
// dipende da quella cortesia.
const CONTESTI = [
  { valore: "PLAYER", etichetta: "Player" },
  { valore: "COACH", etichetta: "Coach" },
];

// Il primo errore del modulo, o `null` se si può inviare.
//
// L'ordine è quello del modulo, dall'alto in basso: chi legge il messaggio
// deve trovare il campo scorrendo la pagina nello stesso verso.
//
// ⚠️ CANTIERE 2 — NON TORNA PIÙ UNA FRASE, TORNA UN AVVISO: `{ chiave }`, e per
// gli errori di un singolo minore anche un `prefisso`, che è il «Child {n}: »
// davanti al motivo. Prima quel prefisso era cucito nella stessa stringa
// (`Child ${n}: please…`); ora le due parti sono due chiavi, e si ricompongono
// nella lingua giusta — in inglese danno la STESSA identica frase di prima,
// perché la chiave del prefisso porta con sé i due punti e lo spazio.
// I casi, il loro ordine e le condizioni che li fanno scattare non sono
// cambiati: questa funzione non ha cambiato una sola regola di validazione.
function primoErroreDelModulo(form) {
  const p = form.parent;

  if (!p.firstName.trim() || !p.lastName.trim() || !p.email.trim() || !p.gender) {
    return { chiave: "family.err.parentRequired" };
  }

  if (!emailBenFormata(p.email)) {
    return { chiave: "family.err.parentEmail" };
  }

  if (!GENERI.some((g) => g.valore === p.gender)) {
    return { chiave: "family.err.parentGender" };
  }

  if (form.ancheIo && !CONTESTI.some((c) => c.valore === form.requestedContext)) {
    return { chiave: "family.err.context" };
  }

  if (!p.acceptTerms) {
    return { chiave: "err.terms" };
  }

  // Variante «sono già iscritti» (ramo dormiente, v. `giaIscritti`): non c'è
  // nessun minore da controllare, e il corpo parte con `children: []`.
  if (form.giaIscritti) return null;

  if (!form.children.length) {
    return { chiave: "family.err.noChildren" };
  }

  if (form.children.length > MAX_FIGLI) {
    return { chiave: "family.err.tooManyChildren", valori: { MAX: MAX_FIGLI } };
  }

  const oggi = oggiAPerth();

  for (let i = 0; i < form.children.length; i++) {
    const figlio = form.children[i];
    const n = i + 1;
    // Il «Child {n}: » davanti a ciascun motivo, in un esemplare solo.
    const prefisso = { chiave: "family.err.childPrefix", valori: { n } };

    if (!figlio.firstName.trim() || !figlio.lastName.trim() || !figlio.dob || !figlio.gender) {
      return { chiave: "family.err.childRequired", prefisso };
    }

    if (!GENERI.some((g) => g.valore === figlio.gender)) {
      return { chiave: "family.err.childGender", prefisso };
    }

    const eta = etaCompiuta(figlio.dob, oggi);

    if (eta === null || eta > 120) {
      return { chiave: "family.err.childDob", prefisso };
    }

    if (eta < 0) {
      return { chiave: "family.err.childDobFuture", prefisso };
    }

    // Stessa legge del server, detta prima: questo percorso è per i MINORI.
    if (eta >= 18) {
      return { chiave: "family.err.childAdult", prefisso };
    }

    if (!figlio.acceptTerms) {
      return { chiave: "family.err.childTerms", prefisso };
    }
  }

  return null;
}

// Che cosa dire di un'iscrizione di famiglia non riuscita.
//
// Si delega tutto al messaggio condiviso (rete caduta, 400 col testo preciso
// del server, 500) TRANNE il 409, e non è un capriccio: su questa rotta il
// duplicato di email può riguardare SOLTANTO il genitore, perché i minori non
// hanno casella propria e il server non ne cerca nessuna. Il testo generico
// del server («Email already registered») lascerebbe il dubbio su quale delle
// persone del modulo sia già in anagrafica: qui la risposta si sa, e dirla è
// più vero che ripetere la frase del server.
function messaggioDiErroreFamiglia(err) {
  if (err && err.status === 409) {
    return { chiave: "family.err.parentEmailTaken" };
  }

  return messaggioDiErrore(err);
}

export default function EnrolFamily({ form, setForm, onIndietro }) {
  const navigate = useNavigate();

  const [touched, setTouched] = useState({});
  // ⚠️ Cantiere 2: `error` non tiene più la frase, tiene l'AVVISO. Vuoto = `null`.
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  // La risposta 201 di /enrol-family: { parent: {...}, children: [...] }. Se
  // c'è, il modulo lascia il posto al pannello di conferma.
  const [esito, setEsito] = useState(null);

  // Lo stato del modulo vive nella pagina (Enrol.jsx) e non qui: così chi
  // torna alla scelta e rientra ritrova quello che aveva scritto.
  const aggiornaGenitore = (campo, valore) => {
    setForm((prev) => ({ ...prev, parent: { ...prev.parent, [campo]: valore } }));
    setTouched((prev) => ({ ...prev, [`parent.${campo}`]: true }));
  };

  const aggiornaFiglio = (indice, campo, valore) => {
    setForm((prev) => ({
      ...prev,
      children: prev.children.map((figlio, i) =>
        i === indice ? { ...figlio, [campo]: valore } : figlio
      ),
    }));
    setTouched((prev) => ({ ...prev, [`child.${indice}.${campo}`]: true }));
  };

  const aggiungiFiglio = () =>
    setForm((prev) =>
      prev.children.length >= MAX_FIGLI
        ? prev
        : { ...prev, children: [...prev.children, figlioVuoto()] }
    );

  const rimuoviFiglio = (indice) =>
    setForm((prev) => ({
      ...prev,
      children: prev.children.filter((_, i) => i !== indice),
    }));

  const segnaToccato = (chiave) =>
    setTouched((prev) => ({ ...prev, [chiave]: true }));

  // Stesso segnale del percorso A: il campo toccato e lasciato vuoto si segna
  // in errore. Le due classi emesse qui le veste ora pages/Enrolment.css: la
  // funzione non è stata toccata, si è vestito ciò che produce.
  const classeCampo = (chiave, valore) => {
    if (!touched[chiave]) return "";
    return valore ? "input-filled" : "input-error";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const problema = primoErroreDelModulo(form);
    if (problema) {
      setError(problema);
      return;
    }

    setSaving(true);

    try {
      // ⚠️ ELENCO CHIUSO, come sul server: partono i soli campi previsti.
      // Nessun `id`, nessun `firebaseUid`, nessuno `status`, nessun
      // `linkedProfiles` e nessun `guardians`: identità, status, minore età e
      // legami di famiglia li decide il server, che di questo corpo legge solo
      // ciò che segue.
      const corpo = {
        parent: {
          firstName: form.parent.firstName.trim(),
          lastName: form.parent.lastName.trim(),
          email: form.parent.email.trim(),
          gender: form.parent.gender,
          acceptTerms: form.parent.acceptTerms,
          // Facoltativo (D7): si manda SOLO se la spunta è attiva. Assente
          // significa «sono soltanto un genitore», ed è il caso normale.
          ...(form.ancheIo && form.requestedContext
            ? { requestedContext: form.requestedContext }
            : {}),
        },
        // Variante D8 (ramo dormiente, v. `giaIscritti`): elenco vuoto, il genitore nasce da solo e i legami con
        // i minori già in anagrafica li farà l'amministrazione.
        children: form.giaIscritti
          ? []
          : form.children.map((figlio) => ({
              firstName: figlio.firstName.trim(),
              lastName: figlio.lastName.trim(),
              dob: figlio.dob,
              gender: figlio.gender,
              acceptTerms: figlio.acceptTerms,
            })),
        // Il flag della variante D8 viaggia con la richiesta (oggi sempre
        // `false`, v. `giaIscritti`): senza, il
        // server non distingue «zero figli perché già iscritti» da «zero
        // figli e basta». È della famiglia, non del genitore: sta accanto
        // a `children`, non dentro `parent`.
        childrenAlreadyEnrolled: form.giaIscritti,
      };

      const risposta = await api.post("/enrol-family", corpo);

      setEsito(risposta || {});
      setForm(famigliaVuota());
      setTouched({});
    } catch (err) {
      setError(messaggioDiErroreFamiglia(err));
    } finally {
      setSaving(false);
    }
  };

  // ATTERRAGGIO DOPO IL 201. Identico al percorso A, e per la stessa ragione:
  // non c'è accesso automatico, perché nessuna utenza è stata creata. Chi si è
  // iscritto entra da sé dalla pagina di accesso, col link via email, e fino
  // all'approvazione atterra sulla vista d'attesa /home/pending.
  //
  // ⚠️ RL-8c: qui, come nel ramo adulto, si prometteva un'email di istruzioni
  // che non parte (POST /enrol-family manda la sola conferma dei termini). E
  // l'accesso di cui si parla è quello del GENITORE: i figli minori non hanno
  // un accesso proprio, e il testo ora lo dice.
  if (esito) {
    const figliCreati = Array.isArray(esito.children) ? esito.children : [];
    const idGenitore = esito.parent && esito.parent.id;

    return (
      <div className="qpsc-page">
        <main className="qpsc-column">
          {/* Nessun avviso: qui non c'è più niente da compilare. */}
          <BarraLingue />

          <header className="qpsc-en-head">
            <img
              src="/images/logo.png"
              alt="QPSC Logo"
              className="qpsc-en-logo"
            />
            <h2 className="qpsc-page-title">
              {t("done.title")}
              <Aiuto k="done.title" colore="titolo" />
            </h2>
          </header>

          <p className="qpsc-en-msg qpsc-en-msg--ok">
            {t("family.done.thanks")}
            <Aiuto k="family.done.thanks" colore="ok" />
          </p>

          {/* ⚠️ IL <strong> RESTA SULL'INGLESE, e con lui l'inglese di questa
              frase, che è composto qui: il dizionario porta le sole righe
              d'aiuto (en: null), in DUE VARIANTI COMPLETE — con figli e solo
              sé — perché una lingua può mettere le parti in un altro ordine e
              un innesto a metà frase non reggerebbe.

              ⚠️ IL PLURALE. In inglese resta il ternario child/children di
              sempre. Nelle tre lingue non serve nessun meccanismo di plurale:
              il cinese non distingue, e le rese araba e hindi del dizionario
              usano una forma che vale per qualunque numero — «{n} من الأطفال»,
              «{n} बच्चे/बच्चों». È la strada scelta apposta per non portare in
              queste pagine una libreria ICU per una frase sola. */}
          <p className="qpsc-body-text">
            Your enrolment is now <strong>PENDING</strong>: a club officer will
            review it.{" "}
            {figliCreati.length
              ? `You enrolled yourself and ${figliCreati.length} ${
                  figliCreati.length === 1 ? "child" : "children"
                }.`
              : "You enrolled yourself only: the club will link you to the children already on record."}{" "}
            You can sign in now with your own email address — until your
            enrolment is approved you will see a waiting screen. Children do
            not sign in themselves: you use the app on their behalf.
            <Aiuto
              k={
                figliCreati.length
                  ? "family.done.pending.withChildren"
                  : "family.done.pending.selfOnly"
              }
              valori={{ n: figliCreati.length }}
              colore="corpo"
            />
          </p>

          <p className="qpsc-body-text">
            {t("family.done.mail")}
            <Aiuto k="family.done.mail" colore="corpo" />
          </p>

          {idGenitore && (
            <p className="qpsc-note">
              {t("done.ref", { id: idGenitore })}
              <Aiuto k="done.ref" valori={{ id: idGenitore }} />
              <br />
              {t("done.refQuote")}
              <Aiuto k="done.refQuote" />
            </p>
          )}

          <button
            className="qpsc-tool-btn qpsc-tool-btn--wide"
            type="button"
            onClick={() => navigate("/login")}
          >
            {t("btn.backToSignInPage")}
          </button>
          <Aiuto k="btn.backToSignInPage" colore="corpo" centrato />
        </main>
      </div>
    );
  }

  return (
    <div className="qpsc-page">
      <main className="qpsc-column">
        {/* Stessa uscita del percorso A, e stessa destinazione: porta al login
            e non indietro nella cronologia. Era una ✕ senza CSS proprio, che
            ricadeva sul tag nudo `button` di pages/UsersList.css e si mostrava
            come un quadrato rosso; ora è l'azione di servizio §5.3. */}
        {/* L'INTERRUTTORE DI LINGUA, in testa al guscio (mockup approvato).
            SENZA AVVISO: nel mockup la riga d'avviso sta sulle sole due
            schermate d'ingresso — l'accesso e la scelta del percorso. Chi è
            arrivato fin qui l'ha già letta, e il modulo non la ripete. */}
        <BarraLingue />

        <button
          className="qpsc-ghost-btn qpsc-en-ghost"
          title="Cancel and go back"
          type="button"
          onClick={() => navigate("/login")}
        >
          &larr; {t("btn.backToSignIn")}
        </button>
        <Aiuto k="btn.backToSignIn" colore="oro" />

        <header className="qpsc-en-head">
          <img
            src="/images/logo.png"
            alt="QPSC Logo"
            className="qpsc-en-logo"
          />
          <h2 className="qpsc-page-title">
            {t("family.title")}
            <Aiuto k="family.title" colore="titolo" />
          </h2>
        </header>

        {/* RL-5b, inclusione motivata (rilievo aperto in RL-5a): a invio in
            volo il ritorno alla scelta era ancora premibile, e chi lo premeva
            si vedeva sparire il modulo mentre la richiesta era già partita.
            Durante `saving` il varco è chiuso, come il pulsante d'invio. */}
        <TornaAllaScelta onIndietro={onIndietro} disabled={saving} />

        <p className="qpsc-body-text">
          {t("family.intro")}
          <Aiuto k="family.intro" colore="corpo" />
        </p>

        <RigaAvviso
          avviso={error}
          className="qpsc-en-msg qpsc-en-msg--error"
          colore="errore"
        />

        <form className="qpsc-en-form" onSubmit={handleSubmit} autoComplete="off">
          <section className="qpsc-en-section">
            <h3 className="qpsc-en-section__title">
              {t("family.parent.section")}
              <Aiuto k="family.parent.section" colore="oro" />
            </h3>

            <label className="qpsc-field">
              <span className="qpsc-field__label">{t("field.firstName")}</span>
              <Aiuto k="field.firstName" />
              <input
                name="parentFirstName"
                value={form.parent.firstName}
                onChange={(e) => aggiornaGenitore("firstName", e.target.value)}
                onBlur={() => segnaToccato("parent.firstName")}
                className={`qpsc-input ${classeCampo(
                  "parent.firstName",
                  form.parent.firstName
                )}`}
                required
              />
            </label>

            <label className="qpsc-field">
              <span className="qpsc-field__label">{t("field.lastName")}</span>
              <Aiuto k="field.lastName" />
              <input
                name="parentLastName"
                value={form.parent.lastName}
                onChange={(e) => aggiornaGenitore("lastName", e.target.value)}
                onBlur={() => segnaToccato("parent.lastName")}
                className={`qpsc-input ${classeCampo(
                  "parent.lastName",
                  form.parent.lastName
                )}`}
                required
              />
            </label>

            {/* Si dice PERCHÉ la si chiede: è l'unica casella della famiglia.
                La nota sta DENTRO la <label> del campo, dove il §8 la vuole
                (`.qpsc-note` sotto il campo), e non come paragrafo staccato. */}
            <label className="qpsc-field">
              <span className="qpsc-field__label">{t("field.email")}</span>
              <Aiuto k="field.email" />
              <input
                type="email"
                name="parentEmail"
                value={form.parent.email}
                onChange={(e) => aggiornaGenitore("email", e.target.value)}
                onBlur={() => segnaToccato("parent.email")}
                className={`qpsc-input ${classeCampo(
                  "parent.email",
                  form.parent.email
                )}`}
                required
              />
              <span className="qpsc-note">
                {t("family.parent.email.note")}
                <Aiuto k="family.parent.email.note" />
              </span>
            </label>

            <label className="qpsc-field">
              <span className="qpsc-field__label">{t("field.gender")}</span>
              {/* Le voci della tendina restano in inglese: vedi Enrol.jsx. */}
              <Aiuto k="field.gender" />
              <select
                name="parentGender"
                value={form.parent.gender}
                onChange={(e) => aggiornaGenitore("gender", e.target.value)}
                onBlur={() => segnaToccato("parent.gender")}
                className={`qpsc-select ${classeCampo(
                  "parent.gender",
                  form.parent.gender
                )}`}
                required
              >
                <option value="">Select</option>
                {GENERI.map((g) => (
                  <option key={g.valore} value={g.valore}>
                    {g.etichetta}
                  </option>
                ))}
              </select>
            </label>
          </section>

          {/* D7 — la spunta «gioco/alleno anch'io». Non assegna nulla: chiede.
              Il server la registra come `requestedContext`, e sarà il club a
              decidere. */}
          <label className="qpsc-check">
            <input
              className="qpsc-check__input"
              type="checkbox"
              name="ancheIo"
              checked={form.ancheIo}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  ancheIo: e.target.checked,
                  // Spenta la spunta, si scorda anche la scelta: altrimenti
                  // resterebbe in memoria un valore che non parte più.
                  requestedContext: e.target.checked ? prev.requestedContext : "",
                }))
              }
            />
            <span className="qpsc-check__box" aria-hidden="true" />
            <span className="qpsc-en-check-text">
              {t("family.alsoMe.check")}
              <Aiuto k="family.alsoMe.check" colore="corpo" />
            </span>
          </label>

          {form.ancheIo && (
            <label className="qpsc-field">
              <span className="qpsc-field__label">{t("family.alsoMe.label")}</span>
              <Aiuto k="family.alsoMe.label" />
              <select
                name="requestedContext"
                value={form.requestedContext}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, requestedContext: e.target.value }))
                }
                onBlur={() => segnaToccato("requestedContext")}
                className={`qpsc-select ${classeCampo(
                  "requestedContext",
                  form.requestedContext
                )}`}
                required
              >
                <option value="">Select</option>
                {CONTESTI.map((c) => (
                  <option key={c.valore} value={c.valore}>
                    {c.etichetta}
                  </option>
                ))}
              </select>
              <span className="qpsc-note">
                {t("family.alsoMe.note")}
                <Aiuto k="family.alsoMe.note" />
              </span>
            </label>
          )}

          <label className="qpsc-check">
            <input
              className="qpsc-check__input"
              type="checkbox"
              name="parentAcceptTerms"
              checked={form.parent.acceptTerms}
              onChange={(e) => aggiornaGenitore("acceptTerms", e.target.checked)}
            />
            <span className="qpsc-check__box" aria-hidden="true" />
            <LinkTermini />
          </label>

          {/* LA SEZIONE DEI MINORI. Sparirebbe del tutto con la variante D8
              (`giaIscritti`, che nel modulo non ha spunta e resta `false`):
              non si disabilita, si toglie — un modulo che mostra campi inerti
              fa credere che siano ancora in gioco. */}
          {!form.giaIscritti && (
            <section className="qpsc-en-section">
              <h3 className="qpsc-en-section__title">
                {t("family.children.section")}
                <Aiuto k="family.children.section" colore="oro" />
              </h3>

              {form.children.map((figlio, indice) => (
                <div className="qpsc-en-child" key={indice}>
                  <h3 className="qpsc-en-section__title">
                    {t("family.child.heading", { n: indice + 1 })}
                    <Aiuto
                      k="family.child.heading"
                      valori={{ n: indice + 1 }}
                      colore="oro"
                    />
                  </h3>

                  <label className="qpsc-field">
                    <span className="qpsc-field__label">{t("field.firstName")}</span>
                    <Aiuto k="field.firstName" />
                    <input
                      name={`childFirstName${indice}`}
                      value={figlio.firstName}
                      onChange={(e) => aggiornaFiglio(indice, "firstName", e.target.value)}
                      onBlur={() => segnaToccato(`child.${indice}.firstName`)}
                      className={`qpsc-input ${classeCampo(
                        `child.${indice}.firstName`,
                        figlio.firstName
                      )}`}
                      required
                    />
                  </label>

                  <label className="qpsc-field">
                    <span className="qpsc-field__label">{t("field.lastName")}</span>
                    <Aiuto k="field.lastName" />
                    <input
                      name={`childLastName${indice}`}
                      value={figlio.lastName}
                      onChange={(e) => aggiornaFiglio(indice, "lastName", e.target.value)}
                      onBlur={() => segnaToccato(`child.${indice}.lastName`)}
                      className={`qpsc-input ${classeCampo(
                        `child.${indice}.lastName`,
                        figlio.lastName
                      )}`}
                      required
                    />
                  </label>

                  <label className="qpsc-field">
                    <span className="qpsc-field__label">{t("field.dob")}</span>
                    <Aiuto k="field.dob" />
                    <input
                      type="date"
                      name={`childDob${indice}`}
                      value={figlio.dob}
                      onChange={(e) => aggiornaFiglio(indice, "dob", e.target.value)}
                      onBlur={() => segnaToccato(`child.${indice}.dob`)}
                      className={`qpsc-input ${classeCampo(
                        `child.${indice}.dob`,
                        figlio.dob
                      )}`}
                      required
                    />
                  </label>

                  <label className="qpsc-field">
                    <span className="qpsc-field__label">{t("field.gender")}</span>
                    <Aiuto k="field.gender" />
                    <select
                      name={`childGender${indice}`}
                      value={figlio.gender}
                      onChange={(e) => aggiornaFiglio(indice, "gender", e.target.value)}
                      onBlur={() => segnaToccato(`child.${indice}.gender`)}
                      className={`qpsc-select ${classeCampo(
                        `child.${indice}.gender`,
                        figlio.gender
                      )}`}
                      required
                    >
                      <option value="">Select</option>
                      {GENERI.map((g) => (
                        <option key={g.valore} value={g.valore}>
                          {g.etichetta}
                        </option>
                      ))}
                    </select>
                  </label>

                  {/* Una spunta PER CIASCUN minore, e il testo dice per conto
                      di chi si firma: sul documento del bambino resterà scritto
                      che ha accettato il tutore. */}
                  <label className="qpsc-check">
                    <input
                      className="qpsc-check__input"
                      type="checkbox"
                      name={`childAcceptTerms${indice}`}
                      checked={figlio.acceptTerms}
                      onChange={(e) =>
                        aggiornaFiglio(indice, "acceptTerms", e.target.checked)
                      }
                    />
                    <span className="qpsc-check__box" aria-hidden="true" />
                    <LinkTermini />
                  </label>
                  {/* ⚠️ QUESTA RIGA SÌ, ED È UNA SCELTA. Le policy restano in
                      inglese (sono la formula legale, e i PDF esistono solo in
                      inglese), ma questa non è una policy: è il CONSENSO PER
                      CONTO DEL MINORE, cioè esattamente ciò che il genitore
                      deve capire prima di spuntare. L'inglese resta la
                      versione ufficiale; sotto compare l'aiuto. */}
                  <p className="qpsc-note">
                    {t("family.child.termsOnBehalf")}
                    <Aiuto k="family.child.termsOnBehalf" />
                  </p>

                  {form.children.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="qpsc-ghost-btn qpsc-en-ghost"
                        onClick={() => rimuoviFiglio(indice)}
                      >
                        &#10005; {t("family.child.remove", { n: indice + 1 })}
                      </button>
                      <Aiuto
                        k="family.child.remove"
                        valori={{ n: indice + 1 }}
                        colore="oro"
                      />
                    </>
                  )}
                </div>
              ))}

              {form.children.length < MAX_FIGLI ? (
                <>
                  <button
                    type="button"
                    className="qpsc-ghost-btn qpsc-en-ghost"
                    onClick={aggiungiFiglio}
                  >
                    + {t("family.child.add")}
                  </button>
                  <Aiuto k="family.child.add" colore="oro" />
                </>
              ) : (
                // Cortesia, non legge: il tetto vero è del server (400).
                <p className="qpsc-note">
                  {t("family.child.maxReached", { MAX: MAX_FIGLI })}
                  <Aiuto k="family.child.maxReached" valori={{ MAX: MAX_FIGLI }} />
                </p>
              )}
            </section>
          )}

          {/* Si dice PRIMA dell'invio che cosa succede dopo: il modulo vecchio
              prometteva un accesso immediato che qui non esiste. */}
          <p className="qpsc-note">
            No password is required: your enrolment is reviewed by the club,
            and you will receive a confirmation email from the club.
          </p>

          <button
            className="qpsc-tool-btn qpsc-tool-btn--wide"
            type="submit"
            disabled={saving}
          >
            {saving ? t("btn.sending") : t("btn.send")}
          </button>
          <Aiuto
            k={saving ? "btn.sending" : "btn.send"}
            colore="corpo"
            centrato
          />
        </form>
      </main>
    </div>
  );
}
